import angular, { IPromise, IQService } from "angular";
import { Ng1StateDeclaration, StateService, Transition } from "angular-ui-router";
import { FeatureManager } from "../../core/feature-flags/feature-manager.service";
import { configureStates, resolveFromParam } from "../../core/router/router-helper.provider";
import { SiteModuleEnum, sortIcdOfficeByOwner, IcdOfficeService, StateProvinceService, IcdCorporationService } from "../../core/services";
import { BankAccountService } from "../../core/services/data-services/bank-account.service";
import { EmployeeService } from "../../core/services/data-services/employee.service";
import { SessionService } from "../../core/services/session.service";
import { UtilityService } from "../../core/services/utility.service";
import { MinimumWageReviewModal } from "../wage-policy/minimum-wage-review-modal.component";
import { ValidNewBankAccount } from "./bank-accounts/add-bank-account.component";
import { UpdatedDirectDeposit } from "./bank-accounts/change-direct-deposit.component";
import { DepositAmountType } from "./bank-accounts/deposit-amount-type";
import { BankAccountVM } from "./bank-accounts/edit-bank-accounts.component";
import { ROSTER_ENABLE_NON_ADMIN_PASSWORD, ROSTER_SEPARATE_EDIT_PAGES } from "./roster-flags";
import { EditRoutePermissions } from "./edit/edit-route-permissions";
import { PermissionGrantService } from "../../core/services/data-services/permission-grant.service";
import { PermissionGrant, EmployeeModel } from "@herbjoy/apex-api-models";
import { PendingEmployeeModal } from "./roster-table/pending-employee-modal.component";
import { OnboardingDocumentService } from "../../core/services/data-services/onboarding-document.service";


const routes: { state: string, config: Ng1StateDeclaration }[] = [{
    state: "user",
    config: {
        url: "/user",
        abstract: true
    }
}, {
    state: "user.listUsers",
    config: {
        url: "/listusers",
        redirectTo: "roster"
    }
}, {
    state: "user.listUsers.office",
    config: {
        url: "/listusers/{officeID}",
        redirectTo: "officeRoster"
    }
}, {
    state: "user.createUser",
    config: {
        url: "/edituser",
        redirectTo: "employee.new"
    }
}, {
    state: "user.editUser",
    config: {
        url: "/edituser/{employeeID:int}",
        redirectTo: "employee.edit"
    }
}, {
    state: "user.ownUserProfile",
    config: {
        url: "/userprofile",
        redirectTo: "account.userProfile"
    }
}, {
    state: "user.userProfile",
    config: {
        url: "/userprofile/{employeeID:int}",
        redirectTo: "employee.edit.userProfile"
    }
}, {
    state: "user.employeeBankInfo",
    config: {
        url: "/employeebankinfo/{employeeID:int}",
        redirectTo: "employee.edit.bankInfo"
    }
}, {
    state: "roster",
    config: {
        name: "roster",
        url: "/roster",
        component: "apexListEmployeesContainer",
        resolve: {
            roleAccess: (employeeService: EmployeeService) => {
                'ngInject';
                return employeeService.retrievePermissions({
                    stateName: "roster"
                });
            },
            onboardingAccess: (employeeService: EmployeeService) => {
                'ngInject';
                return employeeService.retrievePermissions({
                    stateName: "application-status"
                });
            },
            identity: (employeeService: EmployeeService) => {
                'ngInject';
                return employeeService.retrieveIdentity();
            },
            isImpersonated: (sessionService: SessionService) => {
                'ngInject';
                return sessionService.isImpersonated();
            },
            onInitOnboarding: ['$state', ($state) => {
                'ngInject';
                return (params) => {
                    $state.go("roster.pendingModal", params);
                };
            }],
            translations: localeService => {
                'ngInject';
                return localeService.retrieveCurrentLocaleTranslations();
            },
            assignableRoles: (employeeService, translations) => {
                'ngInject';
                return employeeService.retrieveEmployeeAssignableRoles().then(roles => {
                    for (const role of roles) {
                        if (role.Description && role.Description[0] === "|") {
                            role.Description = translations[role.Description.slice(1)]
                        }
                    }
                    return roles;
                });
            },
            permissionGrants: (permissionGrantService: PermissionGrantService) => {
                'ngInject';
                return permissionGrantService.retrievePermissionGrants({});
            },
            //icdCorporation: (identity, icdCorporationService: IcdCorporationService) => {
            //    'ngInject';
            //    if (identity.IcdCorporationID != null) {
            //        return icdCorporationService.retrieveIcdCorporation(identity.IcdCorporationID);
            //    }
            //},
            //onboardingAllowed: (icdCorporationService: IcdCorporationService, identity) => {
            //    'ngInject';
            //    return icdCorporationService.retrieveCorporationModulePermissions(identity.IcdCorporationID);
            //}
        },
        data: {
            pageHeader: "User Listing",
            permissionKey: "navigation:employeesList"
        }
    }
}, {
    state: "roster.pendingModal",
    config: {
        params: {
            employeeId: { type: "string", value: null },
            icdOfficeID: { type: "int", value: null }
        },
        url: "/onboard",
        resolve: {
            icdOfficeId: resolveFromParam("icdOfficeID"),
            employeeId: resolveFromParam("employeeId"),
            isNew: (employeeId) => {
                'ngInject';
                return employeeId == null;
            },
            employeeData: (employeeService: EmployeeService, $transition$: Transition, isNew) => {
                'ngInject';
                if (isNew) {
                    return null;
                }
                const params = $transition$.params();
                return employeeService.retrieveEmployee(params.employeeId);
            },
            employee: (employeeData, assignableRoles, isNew) => {
                'ngInject';
                if (isNew) {
                    return null;
                }
                employeeData.GlobalRoles = assignableRoles
                    .map(function (role) {
                        return {
                            RoleID: role.Id,
                            Name: role.Name,
                            Description: role.Description,
                            IsInRole: !!employeeData.GlobalRoles[role.Id],
                            AdministeredModules: role.AdministeredModules
                        };
                    });
                return employeeData;
            },
            userProfile: (employeeService: EmployeeService, $transition$: Transition, isNew) => {
                'ngInject'
                if (isNew) {
                    return null;
                }
                const params = $transition$.params();
                return employeeService.retrieveEmployeeProfile(params.employeeId);
            },
            stateProvinces: (stateProvinceService: StateProvinceService) => {
                'ngInject';
                return stateProvinceService.retrieveStateProvinces();
            },
            employeeBankInfo: (employeeService: EmployeeService, $transition$: Transition, isNew) => {
                'ngInject';
                if (isNew) {
                    return null;
                }
                const params = $transition$.params();
                return employeeService.retrieveEmployeeBankInfo(params.employeeId);
            },
            telemapperLinked: (employeeService: EmployeeService, $transition$: Transition, isNew) => {
                'ngInject'
                return employeeService.checkForTelemapperLink();
            },
            onboardingApplication: (isNew, employee, onboardingDocumentService: OnboardingDocumentService) => {
                'ngInject';
                if (!isNew) {
                    return onboardingDocumentService.retrieveApplication(employee.UserName);
                } else {
                    return [];
                }
            },
            //icdCorporation: (isNew, employeeId, icdCorporationService: IcdCorporationService) => {
            //    'ngInject';
            //    if (employeeId != null && !isNew) {
            //        return icdCorporationService.retrieveIcdCorporationByEmployee(employeeId);
            //    }
            //},
            icdCorporation: (isNew, employeeId, identity, icdCorporationService: IcdCorporationService) => {
                'ngInject';
                if (!(employeeId != null && !isNew)) {
                    return icdCorporationService.retrieveIcdCorporation(identity.IcdCorporationID);
                } else {
                    return icdCorporationService.retrieveIcdCorporationByEmployee(employeeId);
                }
            },
            addlInputs: (onboardingDocumentService, employeeId) => {
                'ngInject';
                return onboardingDocumentService.retrieveAddlInputs(employeeId);
            },
            changeFormMapping: (onboardingDocumentService) => {
                'ngInject';
                return onboardingDocumentService.retrieveChangeFormMapping();
            }
        },
        onEnter: ['$uibModal', '$state', '$transition$', 'employee', 'userProfile', 'stateProvinces', 'employeeBankInfo', 'telemapperLinked', 'onboardingApplication', 'identity', 'icdCorporation', 'addlInputs', 'changeFormMapping',
            function ($uibModal, $state, $transition$, employee, userProfile, stateProvinces, employeeBankInfo, telemapperLinked, onboardingApplication, identity, icdCorporation, addlInputs, changeFormMapping) {
                const params = $transition$.params();
                $uibModal.open({
                    ...PendingEmployeeModal,
                    resolve: {
                        employeeId: () => params.employeeId,
                        icdOfficeId: () => params.icdOfficeId,
                        employee: () => employee,
                        userProfile: () => userProfile,
                        stateProvinces: () => stateProvinces,
                        employeeBankInfo: () => employeeBankInfo,
                        isNew: () => params.employeeId == null,
                        telemapperLinked: () => telemapperLinked,
                        onboardingApplication: () => onboardingApplication,
                        identity: () => identity,
                        icdCorporation: () => icdCorporation,
                        addlInputs: () => addlInputs,
                        changeFormMapping: () => changeFormMapping
                    },
                }).result.then(function (result) {
                    console.log("Modal closed with result", result);
                }).catch(function (reason) {
                    console.log("Modal dismissed with reason", reason);
                }).finally(() => {
                    $state.go('^');
                });
            }],
        controller: ['$scope', '$state', 'redirectState', 'modalInstance', function ($scope, $state, redirectState, modalInstance) {
            // Use the selectedChild parameter to navigate to the chosen child state
            $scope.$on('modal.closing', function (event, reason, closed) {
                alert('closing');
            });

        }]
    }

}, {
    state: "officeRoster",
    config: {
        url: "/offices/{officeID:int}/roster",
        component: "apexListEmployeesContainer",
        resolve: {
            icdOfficeID: resolveFromParam("officeID"),
            roleAccess: (icdOfficeID, employeeService) => {
                'ngInject';
                return employeeService.retrievePermissions({
                    icdOfficeID: icdOfficeID,
                    module: SiteModuleEnum.Roster,
                    stateName: "roster"
                });
            },
            identity: employeeService => {
                'ngInject';
                return employeeService.retrieveIdentity();
            },
            isImpersonated: sessionService => {
                'ngInject';
                return sessionService.isImpersonated();
            }
        },
        data: {
            pageHeader: "Office Roster",
            permissionKey: "navigation:employeesList"
        }
    }
}, {
    state: "employee",
    config: {
        url: "/employees",
        params: {
            icdOfficeID: { type: "int", value: null }
        },
        abstract: true,
        resolve: {
            showPasswordEditor: (featureManager: FeatureManager) => {
                'ngInject';
                return featureManager.ensureLoaded()
                    .then(featureManager => featureManager.isEnabled(ROSTER_ENABLE_NON_ADMIN_PASSWORD));
            },
            icdOfficeId: $transition$ => {
                'ngInject';
                return $transition$.params().icdOfficeID;
            },
            identity: employeeService => {
                'ngInject';
                return employeeService.retrieveIdentity();
            },
            isImpersonated: sessionService => {
                'ngInject';
                return sessionService.isImpersonated();
            }
        }
    }
}, {
    state: "employee.new",
    config: {
        url: "/new",
        component: "apexEditEmployee",
        params: {
            icdOfficeID: { type: "int", value: null }
        },
        resolve: {
            icdOfficeId: resolveFromParam("icdOfficeID"),
            afterSave: $state => {
                'ngInject';
                return employee => {
                    'ngInject';
                    //commented code for seperate pending profile
                    //return $state.go("employee.edit.pendingProfile", {
                    //    employeeID: employee.EmployeeID
                    //});
                    return $state.go("employee.edit.userProfile", {
                        employeeID: employee.EmployeeID
                    });
                };
            }
        },
        data: {
            pageHeader: "Add Roster Employee",
            permissionKey: "action:createNewEmployee"
        }
    }
}, {
    state: "employee.onboard",
    config: {
        url: "/onboard",
        params: {
            icdOfficeID: { type: "int", value: null }
        },
        resolve: {
            icdOfficeId: resolveFromParam("icdOfficeID"),
            stateProvinces: (stateProvinceService: StateProvinceService) => {
                'ngInject';
                return stateProvinceService.retrieveStateProvinces();
            }
        },
        onEnter: ['$uibModal', '$state', '$transition$', 'stateProvinces', function ($uibModal, $state, $transition$, stateProvinces) {
            'ngInject';
            $uibModal.open({
                ...PendingEmployeeModal,
                resolve: {
                    stateProvinces: () => stateProvinces,
                    isNew: () => true,
                    telemapperLinked: () => false
                }
            }).result.then(function (result) {
                console.log("Modal closed with result", result);
            }).catch(function (reason) {
                console.log("Modal dismissed with reason", reason);
                alert(reason);
            }).finally(() => {
                $state.go('^');
            });
        }],
        data: {
            pageHeader: "Add Roster Employee",
            permissionKey: "action:createNewEmployee"
        }
    }
}, {
    state: "employee.edit",
    config: {
        url: "/{employeeID:int}/edit",
        component: "apexEditEmployeeContainer",
        redirectTo: transition => {
            const featureManager: FeatureManager = transition.injector().get("featureManager");
            const employeeService: EmployeeService = transition.injector().get("employeeService");
            const id = transition.params().employeeID;
            return Promise.all([employeeService.retrieveEmployee(id), featureManager.ensureLoaded()]).then(([employee, _]) => {
                const v2Enabled = featureManager.isEnabled(ROSTER_SEPARATE_EDIT_PAGES);
                return v2Enabled ? "employee.edit.employmentRecord" : "employee.edit.page1";
                /*return employee.EmployeeStatusID === 3 ? "employee.edit.pendingProfile" : v2Enabled ? "employee.edit.employmentRecord" : "employee.edit.page1";*/
            }) as Promise<string>;
        },
        resolve: {
            employeeId: $transition$ => {
                'ngInject';
                return $transition$.params().employeeID;
            },
            featureManagerSync: (featureManager: FeatureManager) => {
                'ngInject';
                return featureManager.ensureLoaded();
            },
            translations: localeService => {
                'ngInject';
                return localeService.retrieveCurrentLocaleTranslations();
            },
            assignableRoles: (employeeService, translations) => {
                'ngInject';
                return employeeService.retrieveEmployeeAssignableRoles().then(roles => {
                    for (const role of roles) {
                        if (role.Description && role.Description[0] === "|") {
                            role.Description = translations[role.Description.slice(1)]
                        }
                    }
                    return roles;
                });
            },
            employeeData: (employeeService: EmployeeService, $transition$: Transition) => {
                'ngInject';
                const { employeeID } = $transition$.params();
                return employeeService.retrieveEmployee(employeeID);
            },
            employee: (employeeData, assignableRoles) => {
                'ngInject';
                employeeData.GlobalRoles = assignableRoles.filter(p => p.GlobalRole)
                    .map(function (role) {
                        return {
                            RoleID: role.Id,
                            Name: role.Name,
                            Description: role.Description,
                            IsInRole: !!employeeData.GlobalRoles[role.Id],
                            AdministeredModules: role.AdministeredModules
                        };
                    });
                return employeeData;
            },
            isPending: (employee) => {
                'ngInject';
                return employee.EmployeeStatusID === 3
            },
            routePermissions: (permissionGrantService: PermissionGrantService): IPromise<EditRoutePermissions> => {
                'ngInject';
                const permissions = permissionGrantService.retrievePermissionGrants({})
                return permissions.then((permissions: PermissionGrant[]) => ({
                    bankAccounts: permissions.some(p => p.Description.Key === "navigation:bankAccounts")
                }));
            },
            onInitOnboarding: ["employeeService", "utilityService", "permissionGrantService", "$q", (employeeService, utilityService, permissionGrantService, $q) => ["$event", ({ employee, onModelStateError }) => {
                if (!employee.HasOnboardingPermission) {
                    return alert("Please reach out to Apex Support: ApexSuppport@herbjoyent.com to enable onboarding for your account.");
                } else {
                    const init = employeeService.initOnboardingEmployee(employee.Id, true)
                        .then(result => {
                            if (!result) {
                                return $q.reject('Initiating onboarding email failed');
                            }
                            const setPermission = permissionGrantService.setOnboardingFormPermissions(employee.Id);
                            return setPermission;
                        });


                    return utilityService.showLoadingUntilResolved(
                        utilityService.alertOnError(init, " Email sending failed"));
                }
            }]],
            onRevokeOnboarding: ["employeeService", "utilityService", "permissionGrantService", (employeeService, utilityService, permissionGrantService) => ["$event", ({ employee, onModelStateError }) => {
                const revoke = permissionGrantService.revokeOnboardingFormPermissions(employee.Id)
                utilityService.showLoadingUntilResolved(
                    utilityService.alertOnError(revoke, "Email sending failed"));
                return revoke;
            }]],
            showOnboarding: (employee) => {
                'ngInject'
                return employee.IsNew;
            }
        },
        data: {
            pageHeader: "Edit Employee",
            permissionKey: "navigation:editRoster"
        }
    }
}, {
    state: "employee.edit.page1",
    config: {
        url: "",
        component: "apexEditEmployee",
        resolve: {
            afterSave: $state => {
                'ngInject';
                return employee => {
                    'ngInject';
                    return $state.go("employee.edit.userProfile", {
                        employeeID: employee.Id
                    });
                };
            }
        }
    }
}, {
    state: "employee.edit.globalRoles",
    config: {
        url: "/global-roles",
        component: "apexUserGlobalRolesEditor"
    }
}, {
    state: "employee.edit.account",
    config: {
        url: "/account",
        views: {
            tabHeader: {
                template: `Apex Account`
            },
            "": {
                component: "apexEmployeeAccountEditor"
            }
        },
        data: {
            title: "Account - Edit Employee"
        }
    }
}, {
    state: "employee.edit.employmentRecord",
    config: {
        url: "/employment-record",
        resolve: {
            icdOffices: (icdOfficeService: IcdOfficeService) => {
                'ngInject';
                return icdOfficeService.retrieveIcdOffices({ moduleID: SiteModuleEnum.Roster })
                    .then(icdOffices => icdOffices.sort(sortIcdOfficeByOwner))
            },
            employmentStatuses: employeeService => {
                'ngInject';
                return employeeService.retrieveEmployeeStatuses();
            },
            stateProvinces: (stateProvinceService: StateProvinceService) => {
                'ngInject'
                return stateProvinceService.retrieveStateProvinces();
            },
            jobTitles: employeeService => {
                'ngInject';
                return employeeService.retrieveJobTitles();
            },
            basisOfPay: employeeService => {
                'ngInject';
                return employeeService.retrieveBasisOfPay();
            },
            payFrequencies: payrollService => {
                'ngInject';
                return payrollService.retrievePayFrequencies();
            },
            departments: employeeService => {
                'ngInject';
                return employeeService.retrieveCompensationProfiles();
            },
            terminationReasons: employeeService => {
                'ngInject';
                return employeeService.retrieveTerminationReasons();
            },
            wagePolicies: wagePolicyService => {
                'ngInject';
                return wagePolicyService.retrieveTodayWagePolicies();
            },
            employeeBankInfo: (employee, employeeService) => {
                'ngInject';
                return employeeService.retrieveEmployeeBankInfo(employee.Id);
            },
            context: (employee, employmentStatuses, icdOffices, jobTitles, payFrequencies, departments,
                terminationReasons, wagePolicies, employmentRecordEditContextFactory, basisOfPay, stateProvinces) => {

                'ngInject';
                return employmentRecordEditContextFactory(employee, icdOffices, employmentStatuses,
                    terminationReasons, payFrequencies, jobTitles, departments, wagePolicies, basisOfPay, stateProvinces)
            },
            onShowMinimumWageRequirements: ["$uibModal", "wagePolicies", ($uibModal, wagePolicies) =>
                ["$event", ({ employmentRecord, employeeBankInfo }) => {
                    const icdOffice = employmentRecord.IcdOffice;
                    if (!icdOffice.WagePolicy) {
                        icdOffice.WagePolicy = wagePolicies.filter(wagePolicy =>
                            wagePolicy.StateProvinceID === employeeBankInfo.StateWorkingIn)[0];
                    }
                    if (!icdOffice.WagePolicy) {
                        icdOffice.WagePolicy = wagePolicies.filter(wagePolicy =>
                            wagePolicy.StateName === icdOffice.OfficeState)[0];
                    }
                    return $uibModal.open({
                        ...MinimumWageReviewModal,
                        resolve: {
                            wagePolicies: () => wagePolicies,
                            selectedOffice: () => icdOffice
                        }
                    }).result;
                }]],
            onSave: ["employeeService", "utilityService", (employeeService, utilityService) => ["$event", ({ employee, employeeBankInfo, onModelStateError }) => {
                const saving = employeeService.saveEmployee(employee, employeeBankInfo);
                utilityService.showLoadingUntilResolved(
                    utilityService.alertOnError(saving, "Saving failed"));
                return saving;
            }]]
            //onInitOnboarding: ["employeeService", "utilityService", "permissionGrantService", "$q", (employeeService, utilityService, permissionGrantService, $q) => ["$event", ({ employee, onModelStateError }) => {

            //    //const init = permissionGrantService.setOnboardingFormPermissions(employee.Id).then((result) => {
            //    //    if (result) {
            //    //        return employeeService.initOnboardingEmployee(employee.Id);
            //    //    }
            //    //    return $q.resolve();
            //    //});

            //    const required = employeeService.checkIfRequiredFilled(employee.Id);
            //    const init = required.then(req => {
            //        if (req != null && !req.Status) {
            //            //show error here and reject promise
            //            return $q.reject(req.ErrorMessage);
            //        }
            //        const startOnboarding = employeeService.initOnboardingEmployee(employee.Id);
            //        return startOnboarding;
            //    }).then(result => {
            //        if (!result) {
            //            return $q.reject('Initiating onboarding email failed');
            //        }
            //        const setPermission = permissionGrantService.setOnboardingFormPermissions(employee.Id);
            //        return setPermission;
            //    });


            //    return utilityService.showLoadingUntilResolved(
            //        utilityService.alertOnError(init, " Email sending failed"));
            //}]],
            //onRevokeOnboarding: ["employeeService", "utilityService", "permissionGrantService", (employeeService, utilityService, permissionGrantService) => ["$event", ({ employee, onModelStateError }) => {
            //    const revoke = permissionGrantService.revokeOnboardingFormPermissions(employee.Id)
            //    utilityService.showLoadingUntilResolved(
            //        utilityService.alertOnError(revoke, "Email sending failed"));
            //    return revoke;
            //}]]
        },
        views: {
            tabHeader: {
                template: `Employment Record`
            },
            "": {
                component: "apexEmploymentRecordListEditor"
            }
        },
        data: {
            title: "Employment Record - Edit Employee"
        }
    }
}, {
    state: "employee.edit.userProfile",
    config: {
        url: "/user-profile",

        resolve: {
            roleAccess: employeeService => {
                'ngInject';
                return employeeService.retrievePermissions({
                    stateName: "roster"
                });
            },
            userProfile: (employeeService: EmployeeService, $transition$: Transition) => {
                'ngInject';
                return employeeService.retrieveEmployeeProfile($transition$.params().employeeID);
            },
            onSaveProfileAndAccount: ["employeeService", "utilityService", (employeeService, utilityService) => ["$event", ({ userProfile, employeeAccount, hasSeparatedEditPages, onModelStateError }) => {
                let saving: any;
                if (!hasSeparatedEditPages) {
                    if (userProfile.Id) {
                        saving = employeeService.updateEmployeeProfile(employeeAccount.Id, userProfile)
                    } else {
                        userProfile.IsNew = false;
                        saving = employeeService.saveEmployee(employeeAccount);
                    }
                } else {
                    userProfile.FirstName = employeeAccount.FirstName;
                    userProfile.LastName = employeeAccount.LastName;
                    userProfile.EmailAddress = employeeAccount.EmailAddress;
                    saving = employeeService.saveEmployee(employeeAccount)
                        .then(() => employeeService.updateEmployeeProfile(employeeAccount.Id, userProfile));
                }
                utilityService.showLoadingUntilResolved(
                    utilityService.alertOnError(saving, "Saving failed"));
                return saving;
            }]],
            onSendPasswordReset: (employeeData: EmployeeModel, employeeService: EmployeeService,
                utilityService: UtilityService) => {
                'ngInject';
                const originalEmailAddress = employeeData.EmailAddress;
                return ["$event", ({ user }) => {
                    if (user.EmailAddress !== originalEmailAddress) {
                        if (!confirm(`The email will be sent to ${originalEmailAddress}. `
                            + ` To send it to ${user.EmailAddress},`
                            + ` save changes before resetting the password.`)) {
                            return;
                        }
                    }

                    utilityService.showLoadingUntilResolved(
                        employeeService.sendPasswordReminder(user.UserName)
                            .then(function () {
                                alert("Password Reset Message Sent");
                            })
                            .catch(function () {
                                alert("Error Sending Password Message");
                            })
                    );
                }];
            }
        },
        views: {
            tabHeader: {
                template: `Employee Profile`
            },
            "": {
                component: "apexEditUserProfile",
            }
        },
        data: {
            title: "User Profile - Edit Employee"
        }
    }
}, {
    state: "employee.edit.bankInfo",
    config: {
        url: "/bank-info",
        views: {
            tabHeader: {
                template: /*html*/`Tax &amp; Bank Info`
            },
            "": {
                component: "apexEditEmployeeBankInfoContainer",
            }
        },
        data: {
            title: "User Tax & Bank Info - Edit Employee"
        }
    }
}, {
    state: "employee.edit.pendingProfile",
    config: {
        url: "/pending-profile",
        views: {
            tabHeader: {
                template: /*html*/`Pending Employee Profile`
            },
            "": {
                component: "apexEmployeeOnboardingProfile",
            }
        },
        data: {
            title: "User Profile (Onboarding) - Edit Employee"
        },
        resolve: {
            icdOffices: (icdOfficeService: IcdOfficeService) => {
                'ngInject';
                return icdOfficeService.retrieveIcdOffices({ moduleID: SiteModuleEnum.Roster })
                    .then(icdOffices => icdOffices.sort(sortIcdOfficeByOwner))
            },
            employmentStatuses: employeeService => {
                'ngInject';
                return employeeService.retrieveEmployeeStatuses();
            },
            jobTitles: employeeService => {
                'ngInject';
                return employeeService.retrieveJobTitles();
            },
            payFrequencies: payrollService => {
                'ngInject';
                return payrollService.retrievePayFrequencies();
            },
            departments: employeeService => {
                'ngInject';
                return employeeService.retrieveCompensationProfiles();
            },
            terminationReasons: employeeService => {
                'ngInject';
                return employeeService.retrieveTerminationReasons();
            },
            wagePolicies: wagePolicyService => {
                'ngInject';
                return wagePolicyService.retrieveTodayWagePolicies();
            },
            basisOfPay: employeeService => {
                'ngInject';
                return employeeService.retrieveBasisOfPay();
            },
            stateProvinces: (stateProvinceService: StateProvinceService) => {
                'ngInject'
                return stateProvinceService.retrieveStateProvinces();
            },
            context: (employee, employmentStatuses, icdOffices, jobTitles, payFrequencies, departments,
                terminationReasons, wagePolicies, basisOfPay, stateProvinces, employmentRecordEditContextFactory) => {
                'ngInject';
                return employmentRecordEditContextFactory(employee, icdOffices, employmentStatuses,
                    terminationReasons, payFrequencies, jobTitles, departments, wagePolicies, basisOfPay, stateProvinces)
            },
            userProfile: (employeeService: EmployeeService, $transition$: Transition) => {
                'ngInject';
                return employeeService.retrieveEmployeeProfile($transition$.params().employeeID);
            },
            onShowMinimumWageRequirements: ["$uibModal", "wagePolicies", ($uibModal, wagePolicies) =>
                ["$event", ({ employmentRecord, employeeBankInfo }) => {
                    const icdOffice = employmentRecord.IcdOffice;
                    if (!icdOffice.WagePolicy) {
                        icdOffice.WagePolicy = wagePolicies.filter(wagePolicy =>
                            wagePolicy.StateProvinceID === employeeBankInfo.StateWorkingIn)[0];
                    }
                    if (!icdOffice.WagePolicy) {
                        icdOffice.WagePolicy = wagePolicies.filter(wagePolicy =>
                            wagePolicy.StateName === icdOffice.OfficeState)[0];
                    }
                    return $uibModal.open({
                        ...MinimumWageReviewModal,
                        resolve: {
                            wagePolicies: () => wagePolicies,
                            selectedOffice: () => icdOffice
                        }
                    }).result;
                }]],
            onSave: ["employeeService", "utilityService", (employeeService, utilityService) => ["$event", ({ userProfile, employeeAccount, onModelStateError }) => {
                let saving: any;
                userProfile.FirstName = employeeAccount.FirstName;
                userProfile.LastName = employeeAccount.LastName;
                userProfile.EmailAddress = employeeAccount.EmailAddress;
                saving = employeeService.saveEmployee(employeeAccount)
                    .then(() => employeeService.updateEmployeeProfile(employeeAccount.Id, userProfile));
                utilityService.showLoadingUntilResolved(
                    utilityService.alertOnError(saving, "Saving failed"));
                return saving;
            }]],
            //onInitOnboarding: ["employeeService", "utilityService", "permissionGrantService", "$q", (employeeService, utilityService, permissionGrantService, $q) => ["$event", ({ employee, onModelStateError }) => {

            //    //const init = permissionGrantService.setOnboardingFormPermissions(employee.Id).then((result) => {
            //    //    if (result) {
            //    //        return employeeService.initOnboardingEmployee(employee.Id);
            //    //    }
            //    //    return $q.resolve();
            //    //});

            //    const required = employeeService.checkIfRequiredFilled(employee.Id);
            //    const init = required.then(req => {
            //        if (req != null && !req.Status) {
            //            //show error here and reject promise
            //            return $q.reject(req.ErrorMessage);
            //        }
            //        const startOnboarding = employeeService.initOnboardingEmployee(employee.Id);
            //        return startOnboarding;
            //    }).then(result => {
            //        if (!result) {
            //            return $q.reject('Initiating onboarding email failed');
            //        }
            //        const setPermission = permissionGrantService.setOnboardingFormPermissions(employee.Id);
            //        return setPermission;
            //    });


            //    return utilityService.showLoadingUntilResolved(
            //        utilityService.alertOnError(init, " Email sending failed"));
            //}]],
            //onRevokeOnboarding: ["employeeService", "utilityService", "permissionGrantService", (employeeService, utilityService, permissionGrantService) => ["$event", ({ employee, onModelStateError }) => {
            //    const revoke = permissionGrantService.revokeOnboardingFormPermissions(employee.Id)
            //    utilityService.showLoadingUntilResolved(
            //        utilityService.alertOnError(revoke, "Email sending failed"));
            //    return revoke;
            //}]],
            genderOptions: (employeeService: EmployeeService) => {
                'ngInject'
                return employeeService.retrieveGenders();
            }
        }
    }
}, {
    state: "employee.edit.bankAccounts",
    config: {
        url: "/bank-accounts",
        params: {
            // Keep track of expanded account state.
            expanded: { type: "int", value: null }
        },
        views: {
            tabHeader: {
                template: "Bank Account Direct Deposit"
            },
            "": {
                component: "apexEditBankAccounts"
            }
        },
        resolve: {
            accounts: ($transition$: Transition, bankAccountService: BankAccountService, employeeId: number):
                IPromise<BankAccountVM[]> => {
                'ngInject';
                const { expanded } = $transition$.params();
                return bankAccountService.retrieveBankAccounts(employeeId)
                    .then(accounts => accounts.map<BankAccountVM>((p, i) => ({
                        bankAccountID: p.BankAccountID,
                        accountNumber: p.AccountNumber,
                        bankName: p.BankName,
                        routingNumber: p.RoutingNumber,
                        institutionNumber: p.InstitutionNumber,
                        ...(p.DirectDepositInformation.length > 0 ?
                            {
                                depositAmount: p.DirectDepositInformation[0].DepositAmount,
                                depositAmountTypeID: p.DirectDepositInformation[0].DepositAmountTypeID,
                                voidCheckUrl: p.DirectDepositInformation[0].VoidCheckUrl,
                                adpDirectDepositUrl: p.DirectDepositInformation[0].AdpDirectDepositUrl
                            } : {
                                depositAmount: null,
                                depositAmountTypeID: null,
                                voidCheckUrl: null,
                                adpDirectDepositUrl: null
                            }),
                        // Expand either the specified account, or the first account in the list.
                        expanded: expanded ? p.BankAccountID === expanded : i === 0
                    })));
            },
            depositAmountTypes: (employeeId: number, bankAccountService: BankAccountService):
                IPromise<DepositAmountType[]> => {
                'ngInject';
                return bankAccountService.retrieveDepositAmountTypes(employeeId);
            },
            onAddAccount: ($q: IQService, $state: StateService, employeeId: number,
                bankAccountService: BankAccountService,
                utilityService: UtilityService) => {
                'ngInject';
                return ["$event", ({ account }: { account: ValidNewBankAccount }) => {
                    const accountCreated = bankAccountService.createBankAccount(employeeId, {
                        AccountNumber: account.accountNumber,
                        BankName: account.bankName,
                        RoutingNumber: account.routingNumber,
                        InstitutionNumber: account.institutionNumber
                    });
                    // Since this isn't an atomic operation, handle partial completion.
                    accountCreated.catch(ex => {
                        alert(`Adding account failed: ${utilityService.getExceptionMessage(ex)}`);
                    });
                    const directDepositConfigured = accountCreated
                        .then(accountID => bankAccountService.configureDirectDeposit(employeeId, accountID, {
                            DepositAmount: account.depositAmount,
                            DepositAmountTypeID: account.depositAmountTypeID,
                            VoidCheck: account.voidCheck,
                            AdpDirectDeposit: account.adpDirectDeposit
                        }).catch(ex => {
                            alert(`Configuring direct deposit failed: ${utilityService.getExceptionMessage(ex)}`);
                        }));
                    const saved = $q.all([accountCreated, directDepositConfigured]);
                    utilityService.showLoadingUntilResolved(saved);
                    // Once all loading has completed, reload the state as long as the account creation succeeded.
                    saved.then(() => accountCreated)
                        // After saving, expand the newly added account.
                        .then(account => $state.go(".", { expanded: account }, { reload: true }));
                }]
            },
            onChangeAccount: ($state: StateService, employeeId: number, bankAccountService: BankAccountService,
                utilityService: UtilityService) => {
                'ngInject';
                return ["$event", ({ account }: { account: UpdatedDirectDeposit }) => {
                    const saved = bankAccountService.configureDirectDeposit(employeeId, account.bankAccountID, {
                        DepositAmount: account.depositAmount,
                        DepositAmountTypeID: account.depositAmountTypeID,
                        VoidCheck: account.voidCheck,
                        AdpDirectDeposit: account.adpDirectDeposit
                    });
                    utilityService.showLoadingUntilResolved(
                        utilityService.alertOnError(saved, "Updating direct deposit failed")
                    );
                    return saved.then(() => {
                        // After saving, ensure the account is expanded.
                        return $state.go(".", { expanded: account.bankAccountID }, { reload: true });
                    });
                }]
            },
            onDeleteAccount: ($state: StateService, employeeId: number, bankAccountService: BankAccountService,
                utilityService: UtilityService) => {
                'ngInject';
                return ["$event", ({ accountID }: { accountID: number }) => {
                    const saved = bankAccountService.removeBankAccount(employeeId, accountID);
                    utilityService.showLoadingUntilResolved(
                        utilityService.alertOnError(saved, "Deleting bank account failed")
                    );
                    // Revert to the default expanded account.
                    return saved.then(() => $state.go(".", { expanded: null }, { reload: true }));
                }]
            },
        },
        data: {
            title: "Bank Accounts - Edit Employee",
            permissionKey: "navigation:bankAccounts"
        }
    }
}, {
    state: "employee.edit.securityRoles",
    config: {
        url: "/security-roles",
        resolve: {
            roles: ($http, apexApiUrl, assignableRoles) => {
                'ngInject';
                // TODO: Switch to service once implementation has been merged in.
                return $http({
                    url: `${apexApiUrl}/v2/roles`
                }).then(response => response.data.filter(role => {
                    return assignableRoles.some(p => p.Id === role.RoleID);
                }));
            },
            user: (employeeService, $transition$) => {
                'ngInject';
                const { employeeID } = $transition$.params();
                return employeeService.retrieveEmployeeSecuritySettings(employeeID)
                    .then(user => ({
                        EmployeeID: user.EmployeeID,
                        FirstName: user.FirstName,
                        LastName: user.LastName,
                        Roles: user.Roles.map(p => p.RoleID),
                        RolesAtDivisions: user.RolesAtDivisions.map(p => ({
                            RoleID: p.Role.RoleID,
                            DivisionID: p.Division.DivisionID
                        })),
                        RolesAtOffices: user.RolesAtOffices.map(p => ({
                            RoleID: p.Role.RoleID,
                            IcdOfficeID: p.Office.IcdOfficeID
                        })),
                    }));
            },
            divisions: divisionService => {
                'ngInject';
                return divisionService.retrieveDivisions()
                    .then(p => p.sort((a, b) => a.Name < b.Name ? -1 : 1));
            },
            icdOffices: (icdOfficeService: IcdOfficeService) => {
                'ngInject';
                return icdOfficeService.retrieveIcdOffices({ moduleID: SiteModuleEnum.Roster })
                    .then(icdOffices => icdOffices.sort(sortIcdOfficeByOwner))
            },
            claims: (permissionGrantService, user) => {
                'ngInject';
                return permissionGrantService.retrieveUserClaims(user.EmployeeID);
            },
            permissionDescriptions: permissionGrantService => {
                'ngInject';
                return permissionGrantService.retrievePermissionDescriptions();
            },
            claimPermissions: (permissionGrantService, claims) => {
                'ngInject';
                return permissionGrantService.simulationPermissionGrantsForClaims(claims);
            },
            userPermissions: (permissionGrantService, user) => {
                'ngInject';
                return permissionGrantService.retrieveUserPermissions(user.EmployeeID);
            }
        },
        views: {
            tabHeader: {
                template: "Security Roles"
            },
            "": {
                component: "apexUserSecurityEditor"
            }
        },
        data: {
            title: "Security Roles - Edit Employee"
        }
    }
}];

/**
 * An angularjs module containing routes definitions for roster listing and edit pages.
 */
export const RosterRoutingModule = angular
    .module("apex.mainApp.roster.routing", [])
    .run(configureStates(routes))
    .name;
